import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useLayoutEffect, useState, useRef, useEffect, useCallback} from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Node,
  XYPosition,
  useEdgesState,
  useNodesState,
  MarkerType,
  ReactFlowInstance,
} from 'reactflow';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {
  StudyInaccessibleContentBanner,
  StudyQueryErrorBanner,
} from 'studies/typed/utils/ui/StudyQueryBanners';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls from 'ui-library/reactflow/controls/HorizontalControls';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
  sizeToWidth,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar, {FLOWCHART_SIDE_PANEL_WIDTH} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';
import {assertNever} from 'util/Types';

import GroupFlowchartNode, {GROUP_TITLE_GAP, NodeType as GroupFlowchartNodeType} from './GroupFlowchartNode';
import UnitOperationFlowchartNode, {NodeType} from './UnitOperationFlowchartNode';
import {getMethodId} from './UnitOpLinkingFlowchartUtils';
import {
  getDestinationNodeId,
  getOrderedDestPMEIsInColumnsPerProcedureRun,
  makeDestinationPMEINode,
  makeIntraRunMethodEdges,
  makeUnitOpLinkEdge,
  getNodePositionsByPMEIIdForProcedureRun,
  NODE_HORIZONTAL_GAP,
  PROCEDURE_RUN_HORIZONTAL_GAP,
  UnitOpLinkEdge,
} from './UnitOpLinkingFlowchartUtils';
import UnitOpLinkingSelectTree from './UnitOpLinkingSelectTree';
import {UnitOperationFields} from './useUnitOpLinkingState';
import useUnitOpLinkingStudyState, {UnitOpLinkingStudyStateStatus} from './useUnitOpLinkingStudyState';

const LEFT_SIDEPANEL_SIZE = SidepanelSize.SMALL;
const LEFT_SIDEPANEL_WIDTH = sizeToWidth[LEFT_SIDEPANEL_SIZE] as number;

const NODE_TYPES = {
  [NodeType.DESTINATION_PMEI]: UnitOperationFlowchartNode,
  [GroupFlowchartNodeType.HOVER_GROUP]: GroupFlowchartNode,
  [GroupFlowchartNodeType.SELECTION_GROUP]: GroupFlowchartNode,
};

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesConnectable: false,
  nodesDraggable: false,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 100, y: 100, zoom: 1},
  minZoom: 0.1,
  proOptions: REACT_FLOW_PRO_OPTIONS,
};

const GROUP_TITLE_TOTAL_HEIGHT = GROUP_TITLE_GAP + 20; // Approximate height of title + gap

const NODE_GROUP_PADDING = 4;

const INTRA_RUN_METHOD_EDGE_STYLE = {
  markerEnd: {type: MarkerType.Arrow, strokeWidth: 1.5, color: Color.GRAY_600},
  style: {strokeWidth: 1.5, stroke: Color.GRAY_600},
};

const INTER_RUN_LINK_EDGE_STYLE = {
  markerEnd: {type: MarkerType.Arrow, strokeWidth: 1.5, color: Color.GRAY_600},
  style: {strokeWidth: 1.5, stroke: Color.GRAY_600, strokeDasharray: '4, 4'},
};

interface UnitOpLinkingFlowchartProps {
  studyId: string;
}

export default function QueryingUnitOpLinkingStudyFlowchart(props: UnitOpLinkingFlowchartProps): JSX.Element {
  const unitOpLinkingState = useUnitOpLinkingStudyState(props.studyId);
  if (unitOpLinkingState.status === UnitOpLinkingStudyStateStatus.QUERY_ERROR) {
    return <StudyQueryErrorBanner />;
  } else if (unitOpLinkingState.status === UnitOpLinkingStudyStateStatus.QUERY_PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (unitOpLinkingState.status === UnitOpLinkingStudyStateStatus.STUDY_INACCESSIBLE) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={unitOpLinkingState.inaccessibleStudy}
        modelType={ModelTypeConstants.STUDY}
        displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
      />
    );
  } else if (
    unitOpLinkingState.status === UnitOpLinkingStudyStateStatus.CONTAINS_PROCEDURE_RUN_WITH_EXPANDED_FLOWCHART
  ) {
    const procedureTypeString = getProcedureRunUIStringForStudyType(unitOpLinkingState.studyType, {
      plural: true,
    });
    return (
      <div className={css(styles.bannerWrapper)}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={`This study contains old ${procedureTypeString} that can not be visualized.`}
        />
      </div>
    );
  } else if (unitOpLinkingState.status === UnitOpLinkingStudyStateStatus.SUCCESS) {
    return (
      <UnitOpLinkingStudyFlowchart
        unitOperations={unitOpLinkingState.pmeis}
        interRunLinks={unitOpLinkingState.interProcedureLinks}
        studyType={unitOpLinkingState.studyType}
        // Force component remount when PMEIs change after query re-run to ensure ReactFlow state,
        // internal lookup maps, and layout calculations are properly reset with fresh data
        key={unitOpLinkingState.pmeis.map((pmei) => pmei.pmeiId).join(',')}
      />
    );
  } else {
    assert(false, `Unexpected status ${assertNever(unitOpLinkingState.status)}`);
    return <></>;
  }
}

export function UnitOpLinkingStudyFlowchart({
  unitOperations,
  interRunLinks,
  studyType,
}: {
  unitOperations: Array<UnitOperationFields>;
  interRunLinks: Array<UnitOpLinkEdge>;
  studyType: StudyType;
}): JSX.Element {
  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <UnitOpLinkingStudyFlowchartPresentational
          unitOperations={unitOperations}
          interRunLinks={interRunLinks}
          studyType={studyType}
        />
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

export function UnitOpLinkingStudyFlowchartPresentational({
  unitOperations,
  interRunLinks,
  studyType,
}: {
  unitOperations: Array<UnitOperationFields>;
  interRunLinks: Array<UnitOpLinkEdge>;
  studyType: StudyType;
}): JSX.Element {
  const showEmptyState = useMemo(() => unitOperations.length === 0, [unitOperations]);
  const [isLeftSidepanelOpen, setIsLeftSidepanelOpen] = useState(true);
  const [selectedSidebarIconIndex, setSelectedSidebarIconIndex] = useState<number | null>(0);
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [hoveringItemId, setHoveringItemId] = useState<string | null>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const orderedPMEIsInColumnsPerProcedureRun = useMemo(
    () => getOrderedDestPMEIsInColumnsPerProcedureRun(unitOperations),
    [unitOperations]
  );

  // Maps for quick lookup of nodes by different IDs
  const {pmeiIdSet, methodIdToPMEIs, procedureRunIdToPMEIs} = useMemo(() => {
    const pmeiIdSet = new Map<string, UnitOperationFields>();
    const methodIdToPMEIs = new Map<string, Array<UnitOperationFields>>();
    const procedureRunIdToPMEIs = new Map<string, Array<UnitOperationFields>>();
    unitOperations.forEach((pmei) => {
      pmeiIdSet.set(pmei.pmeiId, pmei);
      const methodId = getMethodId(pmei.procedureRunId, pmei.templateMethodId);
      if (!methodIdToPMEIs.has(methodId)) {
        methodIdToPMEIs.set(methodId, []);
      }
      methodIdToPMEIs.get(methodId)!.push(pmei);
      if (!procedureRunIdToPMEIs.has(pmei.procedureRunId)) {
        procedureRunIdToPMEIs.set(pmei.procedureRunId, []);
      }
      procedureRunIdToPMEIs.get(pmei.procedureRunId)!.push(pmei);
    });
    return {pmeiIdSet, methodIdToPMEIs, procedureRunIdToPMEIs};
  }, [unitOperations]);

  // Pan to selected nodes when selection changes
  useEffect(() => {
    if (reactFlowInstance.current && selectedIds.length > 0) {
      // Get the node IDs that correspond to the selected items
      const nodeIdsToFit: Array<string> = [];

      for (const selectedId of selectedIds) {
        // Check if selected item is a PMEI (individual node)
        if (pmeiIdSet.has(selectedId)) {
          nodeIdsToFit.push(getDestinationNodeId(selectedId));
          continue;
        }

        // Check if selected item is a method group
        const methodPMEIs = methodIdToPMEIs.get(selectedId);
        if (methodPMEIs) {
          nodeIdsToFit.push(...methodPMEIs.map((pmei) => getDestinationNodeId(pmei.pmeiId)));
          continue;
        }

        // Check if selected item is a procedure run
        const procedureRunPMEIs = procedureRunIdToPMEIs.get(selectedId);
        if (procedureRunPMEIs) {
          nodeIdsToFit.push(...procedureRunPMEIs.map((pmei) => getDestinationNodeId(pmei.pmeiId)));
        }
      }

      if (nodeIdsToFit.length > 0) {
        if (reactFlowInstance.current) {
          reactFlowInstance.current.fitView({
            nodes: nodeIdsToFit.map((id) => ({id})),
            padding: 0.2,
            duration: 800,
            includeHiddenNodes: false,
          });
        }
      }
    }
  }, [selectedIds, unitOperations, isLeftSidepanelOpen, pmeiIdSet, methodIdToPMEIs, procedureRunIdToPMEIs]);

  const getGroupNodeId = useCallback(
    (prefix: string, hoverOrSelectedId: string): string | null => {
      if (
        pmeiIdSet.has(hoverOrSelectedId) ||
        methodIdToPMEIs.has(hoverOrSelectedId) ||
        procedureRunIdToPMEIs.has(hoverOrSelectedId)
      ) {
        return `${prefix}-${hoverOrSelectedId}`;
      }
      return null;
    },
    [pmeiIdSet, methodIdToPMEIs, procedureRunIdToPMEIs]
  );

  const getGroupTitle = useCallback(
    (itemId: string): string => {
      // Check if it's a PMEI
      const pmei = pmeiIdSet.get(itemId);
      if (pmei) {
        return `${pmei.conditionName} - ${pmei.replicateNumber}`;
      }

      // Check if it's a method group
      const methodPMEIs = methodIdToPMEIs.get(itemId);
      if (methodPMEIs) {
        return methodPMEIs[0].methodName;
      }

      // Check if it's a procedure run
      const procedureRunPMEIs = procedureRunIdToPMEIs.get(itemId);
      if (procedureRunPMEIs) {
        const pmei = procedureRunPMEIs[0];
        return pmei.procedureRunDisplayId
          ? `${pmei.procedureRunName} (${pmei.procedureRunDisplayId})`
          : pmei.procedureRunName || 'Procedure Run';
      }

      return 'Group';
    },
    [pmeiIdSet, methodIdToPMEIs, procedureRunIdToPMEIs]
  );

  // Helper function to create group nodes
  const createGroupNodes = useCallback(
    (hoveringId: string | null, selectedIds: Array<string>): Array<Node> => {
      const groups: Array<Node> = [];

      // Create hover group if hovering over a node that is not selected
      const hoverGroupId =
        hoveringId && !selectedIds.includes(hoveringId) ? getGroupNodeId('hover-group', hoveringId) : null;
      if (hoverGroupId && hoveringId) {
        groups.push({
          id: hoverGroupId,
          type: GroupFlowchartNodeType.HOVER_GROUP,
          position: {x: 0, y: 0}, // Will be calculated based on child node position
          data: {
            title: getGroupTitle(hoveringId),
            isHover: true,
          },
        });
      }

      // Create selection groups
      for (const selectedId of selectedIds) {
        const selectionGroupId = getGroupNodeId('selection-group', selectedId);
        if (selectionGroupId) {
          groups.push({
            id: selectionGroupId,
            type: GroupFlowchartNodeType.SELECTION_GROUP,
            position: {x: 0, y: 0},
            data: {
              title: getGroupTitle(selectedId),
              isHover: false,
            },
          });
        }
      }

      return groups;
    },
    [getGroupNodeId, getGroupTitle]
  );

  const [edges] = useEdgesState<UnitOpLinkEdge>([
    ...orderedPMEIsInColumnsPerProcedureRun.flatMap((procedureRun) =>
      makeIntraRunMethodEdges(procedureRun).map((edge) => ({
        ...edge,
        ...INTRA_RUN_METHOD_EDGE_STYLE,
      }))
    ),
    ...interRunLinks.map((link) => ({
      ...makeUnitOpLinkEdge(link),
      source: getDestinationNodeId(link.sourcePMEIId),
      ...INTER_RUN_LINK_EDGE_STYLE,
    })),
  ]);

  const [nodes, setNodes, onNodesChange] = useNodesState(
    unitOperations.map((node) => makeDestinationPMEINode(node, {selectable: false}))
  );

  // Detect if node membership (add/remove) or dimensions (populated by reactflow) have changed
  const nodeLayoutDepsHash = JSON.stringify(
    nodes.map(({data: {pmeiId}, height, width}) => ({pmeiId, height, width}))
  );

  // Calculate positions for the nodes.
  useLayoutEffect(
    () => {
      // Calculate positions for each procedure run, starting from the left
      let currentX = 0;
      const positionsByPMEIId: Record<string, XYPosition> = {};

      for (const procedureRun of orderedPMEIsInColumnsPerProcedureRun) {
        // Get nodes for this procedure run
        const procedureRunPMEIIds = new Set(
          procedureRun.flatMap((methodColumn) => methodColumn.map((pmei) => pmei.pmeiId))
        );
        const procedureRunNodes = nodes.filter((node) => procedureRunPMEIIds.has(node.data.pmeiId));

        // Calculate positions for this procedure run
        const {positionsByPMEIId: positionsByPMEIIdForProcedureRun} = getNodePositionsByPMEIIdForProcedureRun(
          procedureRunNodes,
          procedureRun,
          currentX
        );

        // Merge positions
        Object.assign(positionsByPMEIId, positionsByPMEIIdForProcedureRun);

        // Calculate the width of this procedure run to determine starting X for next run
        const procedureRunWidth = getProcedureRunWidth(procedureRunNodes, procedureRun);
        currentX += procedureRunWidth + PROCEDURE_RUN_HORIZONTAL_GAP;
      }

      // Create and update group node positions based on their children
      const groupNodes = createGroupNodes(hoveringItemId, selectedIds);
      const updatedGroupNodes = groupNodes.map((groupNode: Node) => {
        // Get all nodes that belong to this group based on the group ID
        let groupNodeIds: Array<string> = [];

        if (groupNode.id.startsWith('hover-group-') || groupNode.id.startsWith('selection-group-')) {
          // Remove the prefix from the group ID to get the ID of the node it represents
          const groupId = groupNode.id.replace('hover-group-', '').replace('selection-group-', '');

          // Check what type of node the group represents
          const pmeiNode = pmeiIdSet.get(groupId);
          const methodPMEIs = methodIdToPMEIs.get(groupId);
          const procedureRunPMEIs = procedureRunIdToPMEIs.get(groupId);
          if (pmeiNode) {
            groupNodeIds = [pmeiNode.pmeiId];
          } else if (methodPMEIs) {
            groupNodeIds = methodPMEIs.map((pmei) => pmei.pmeiId);
          } else if (procedureRunPMEIs) {
            groupNodeIds = procedureRunPMEIs.map((pmei) => pmei.pmeiId);
          }
        }

        if (groupNodeIds.length === 0) {
          return groupNode;
        }

        // Calculate bounding box based on all nodes in the group
        const groupNodes = nodes.filter((node) => groupNodeIds.includes(node.data.pmeiId));
        const minX = Math.min(...groupNodes.map((node) => positionsByPMEIId[node.data.pmeiId]?.x || 0));
        const maxX = Math.max(
          ...groupNodes.map((node) => (positionsByPMEIId[node.data.pmeiId]?.x || 0) + (node.width || 0))
        );
        const minY = Math.min(...groupNodes.map((node) => positionsByPMEIId[node.data.pmeiId]?.y || 0));
        const maxY = Math.max(
          ...groupNodes.map((node) => (positionsByPMEIId[node.data.pmeiId]?.y || 0) + (node.height || 0))
        );

        return {
          ...groupNode,
          position: {x: minX - NODE_GROUP_PADDING, y: minY - NODE_GROUP_PADDING - GROUP_TITLE_TOTAL_HEIGHT}, // Account for title height
          data: {
            ...groupNode.data,
            width: maxX - minX + NODE_GROUP_PADDING * 2,
            height: maxY - minY + NODE_GROUP_PADDING * 2,
          },
        };
      });

      setNodes((prevNodes) => {
        const nonGroupNodes = prevNodes.filter(
          (node) =>
            node.type !== GroupFlowchartNodeType.HOVER_GROUP &&
            node.type !== GroupFlowchartNodeType.SELECTION_GROUP
        );
        // Sort nodes so that group nodes (parents) come before their children
        const sortedNodes = [
          ...updatedGroupNodes.sort(sortGroupNodes),
          ...nonGroupNodes.map((node) => ({
            ...node,
            position: positionsByPMEIId[node.data.pmeiId] || {x: 0, y: 0},
          })),
        ];

        return sortedNodes;
      });
    },
    // Can't use nodes as dependency because the reference changes every time state is set,
    // resulting in an infinite loop. Instead use nodeLayoutDepsHash to only detect changes we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitOperations, nodeLayoutDepsHash, hoveringItemId, selectedIds]
  );

  return (
    <div
      className={css(styles.fullScreenContainer)}
      {...getTestProps({component: 'UnitOpLinkingStudyFlowchart', element: 'root'})}
    >
      <div
        className={css(styles.leftSidepanelContainer)}
        {...getTestProps({component: 'UnitOpLinkingStudyFlowchart', element: 'leftSidepanelContainer'})}
      >
        <FlowchartSidebar
          icons={[
            {
              name: 'multi-select',
              onClick: () => {
                setIsLeftSidepanelOpen(true);
              },
              tooltip: 'Overview',
            },
          ]}
          selectedIconIndex={selectedSidebarIconIndex}
          setSelectedIconIndex={setSelectedSidebarIconIndex}
        />
        <FlowchartContextPanel
          panelSide={PanelSide.LEFT}
          sidepanelName="Overview"
          isOpen={isLeftSidepanelOpen}
          onClose={() => {
            setIsLeftSidepanelOpen(false);
            setSelectedSidebarIconIndex(null);
          }}
          size={SidepanelSize.SMALL}
          showExpandCollapseButtons={false}
        >
          <div
            className={css(styles.selectTreeContainer)}
            {...getTestProps({component: 'UnitOpLinkingStudyFlowchart', element: 'selectTreeContainer'})}
          >
            <UnitOpLinkingSelectTree
              studyType={studyType}
              allPMEIs={unitOperations}
              setHoveringItemId={setHoveringItemId}
              selectedIds={selectedIds}
              setSelectedIds={(ids) => {
                if (selectedIds.includes(ids[0])) {
                  setSelectedIds([]);
                } else {
                  setSelectedIds(ids);
                }
              }}
            />
          </div>
        </FlowchartContextPanel>
      </div>
      <div
        style={{
          flexGrow: 1,
          marginLeft: isLeftSidepanelOpen ? LEFT_SIDEPANEL_WIDTH : 0,
          position: 'relative',
        }}
        {...getTestProps({component: 'UnitOpLinkingStudyFlowchart', element: 'flowchart'})}
      >
        <ReactFlow
          nodes={nodes}
          onNodesChange={onNodesChange}
          edges={edges}
          nodeTypes={NODE_TYPES}
          onInit={(instance) => {
            reactFlowInstance.current = instance;
          }}
          {...STATIC_PROPS}
          // Disable interactions when there are no nodes
          panOnDrag={!showEmptyState}
          panOnScroll={!showEmptyState}
          zoomOnPinch={!showEmptyState}
        >
          {!showEmptyState && <HorizontalControls shiftLeft={FLOWCHART_SIDE_PANEL_WIDTH} />}
          <DefaultBackground />
        </ReactFlow>
        {showEmptyState && <UnitOpLinkingStudyFlowchartEmptyView studyType={studyType} />}
      </div>
    </div>
  );
}

export function UnitOpLinkingStudyFlowchartEmptyView({studyType}: {studyType: StudyType}): JSX.Element {
  return (
    <div className={css(styles.emptyStateOverlay)}>
      <img src={staticURL('/static/img/studies/unit-op-instance-linking/EmptyFlowchart.svg')} />
      <div className={css(styles.emptyStateText)}>
        All {getProcedureRunUIStringForStudyType(studyType, {plural: true})} will be visualized here.
      </div>
    </div>
  );
}

/**
 * Calculate the total width of a procedure run including all its method columns and gaps.
 */
function getProcedureRunWidth(
  procedureRunNodes: Array<Node>,
  procedureRun: Array<Array<UnitOperationFields>>
): number {
  const nodeByPMEIId = new Map(procedureRunNodes.map((node) => [node.data.pmeiId, node]));
  let totalWidth = 0;

  for (let i = 0; i < procedureRun.length; i++) {
    const methodColumn = procedureRun[i];
    const maxNodeWidth = Math.max(...methodColumn.map((pmei) => nodeByPMEIId.get(pmei.pmeiId)?.width ?? 0));
    totalWidth += maxNodeWidth;

    // Add gap between method columns (except for the last one)
    if (i < procedureRun.length - 1) {
      totalWidth += NODE_HORIZONTAL_GAP;
    }
  }

  return totalWidth;
}

/**
 * Sort group nodes so that hover groups come after selection groups
 */
function sortGroupNodes(a: Node, b: Node): number {
  const aIsHover = a.id.startsWith('hover-group-');
  const bIsHover = b.id.startsWith('hover-group-');
  if (aIsHover && !bIsHover) {
    return 1;
  }
  if (!aIsHover && bIsHover) {
    return -1;
  }
  return 0;
}

const styles = StyleSheet.create({
  fullScreenContainer: {
    margin: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  loadingDiv: {paddingBottom: 5},
  leftSidepanelContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    // Ensure left sidepanel renders on top of reactflow canvas
    // Instead of shifting reactflow canvas when left sidepanel is open
    zIndex: 10,
  },
  selectTreeContainer: {height: '100%'},
  bannerWrapper: {
    margin: 8,
    width: '100%',
    display: 'flex',
  },
  emptyStateOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyStateText: {
    ...FONT_PROXIMA_13,
    color: Color.GRAY_700,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    textAlign: 'center',
    marginTop: '16px',
  },
});
